import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { useQuery } from '@tanstack/react-query'
import React from 'react'

import { ApiResponse } from '@/api/services/CustomerService'
import { CashflowScoreService } from '@/api/services/CashflowScoreService'
import { DataTable } from '@/components/UI/DataTable'
import { ITableColumn } from '@/components/UI/DataTable/TableHead'
import { Paginator } from '@/components/UI/Paginator'
import SkeletonRow from '@/components/UI/Skeleton'
import { ErrorPage } from '@/pages/Error'
import { useAppStore } from '@/store'
import { ICashflowScore } from '@/types'
import { safeFormatDate } from '@/utils/common'

const columns: ITableColumn<ICashflowScore>[] = [
  {
    key: 'createdAt',
    id: 'createdAt',
    label: 'Scoring Date',
    minWidth: '150px',
    render: value => <StyledText>{safeFormatDate(value)}</StyledText>,
  },
  {
    key: 'score',
    id: 'score',
    label: 'Score',
    render: value => (
      <StyledText>{typeof value === 'number' ? Math.round(value) : (value ?? '—')}</StyledText>
    ),
  },
  {
    key: 'borrowerProfile',
    id: 'borrowerProfile',
    label: 'Borrower Profile',
    render: value => <StyledText>{value}</StyledText>,
  } 
]

const renderNestedContent = (row: ICashflowScore) => {
  const drivers = row.drivers ?? []

  return (
    <NestedContentContainer>
      {drivers.length > 0 ? (
        <TableContainer>
          <Table size="small" aria-label="Cashflow Score Drivers">
            <TableHead>
              <TableRow>
                <TableCell>Driver</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drivers.map((driver, index) => (
                <TableRow key={`${driver.name}-${driver.direction}-${index}`}>
                  <TableCell>
                    <StyledText>{driver.name}</StyledText>
                  </TableCell>
                  <TableCell>
                    <StyledText>{driver.direction}</StyledText>
                  </TableCell>
                  <TableCell>
                    <StyledText>{driver.value}</StyledText>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <StyledText>No drivers found for this score.</StyledText>
      )}
    </NestedContentContainer>
  )
}

export const CashflowScore: React.FC = () => {
  const { user, cashflowPage, setCashflowPage } = useAppStore()
  const userId = user?.id

  // Avoid carrying filter/pagination state across customers.
  React.useEffect(() => {
    setCashflowPage(1)
  }, [userId])

  const { data, isLoading, isError } = useQuery<ApiResponse<ICashflowScore>>({
    // userId is part of the key so a different customer never reads cached cashflow scores.
    queryKey: ['cashflow-scores', userId, cashflowPage],
    queryFn: () => CashflowScoreService.fetchCashflowScores({ page: cashflowPage }),
    enabled: !!userId,
    placeholderData: prev => prev,
  })

  const Header = (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
      <Typography variant="h6">Cashflow Scores</Typography>
    </Box>
  )

  if (!userId || isLoading) {
    return (
      <>
        {Header}
        <SkeletonRow columns={columns} />
      </>
    )
  }

  const cashflowScores = data?.data ?? []
  const hasCashflowScores = cashflowScores.length > 0

  return (
    <>
      {Header}

      {isError ? (
        <ErrorPage
          title="Unable to Fetch Cashflow Scores"
          message="We couldn't retrieve cashflow score data at the moment. Please check your internet connection or try again later. If the issue persists, contact administrator."
        />
      ) : hasCashflowScores ? (
        <DataTable<ICashflowScore> rows={cashflowScores} columns={columns} renderNestedContent={renderNestedContent} />
      ) : (
        <EmptyContainer>
          <Message>
            {'No cashflow scores found for this customer.'}
          </Message>
        </EmptyContainer>
      )}

      {!isError && Number(data?.pagination?.totalRecords ?? 0) > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          mt={2}
          py={1}
          sx={{
            border: '1px solid #E4E7EC',
            borderRadius: '8px',
          }}
        >
          <Paginator
            totalPages={Number(data?.pagination?.totalPages ?? 1)}
            currentPage={cashflowPage}
            onChange={setCashflowPage}
          />
        </Box>
      )}
    </>
  )
}

const StyledText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  textAlign: 'left',
  color: theme.palette.secondary.main,
}))

const NestedContentContainer = styled(Box)({
  padding: '16px 46px',
})

const EmptyContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  border: '1px solid #E4E7EC',
  borderRadius: '8px',
  padding: '3rem 0',
})

const Message = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  fontWeight: 500,
  maxWidth: 600,
  textAlign: 'center',
}))
